import { cssZoomOf, eventTargetElement, visualToCss } from '@/common/utils/dialogDragPosition'
import { getUiScale } from '@/common/utils/uiScale'
import { onUnmounted, type Ref } from 'vue'

export const PLATFORM_DIALOG_DRAGGING_CLASS = 'platform-dialog-dragging'

export interface UsePlatformDialogDragOptions {
  enabled?: Ref<boolean>
}

interface DragSession {
  dialogEl: HTMLElement
  zoom: number
  originX: number
  originY: number
  pointerX: number
  pointerY: number
}

const setDocumentDragging = (active: boolean) => {
  if (typeof document === 'undefined') return
  document.documentElement.classList.toggle(PLATFORM_DIALOG_DRAGGING_CLASS, active)
}

const resolveDialogFromEvent = (event: Event): HTMLElement | null => {
  const target = eventTargetElement(event.target)
  if (!target) return null
  if (target.closest('button, a, input, textarea, select, [data-no-overlay-drag]')) return null

  const header = target.closest(
    '.p-dialog-header, .dialog-header, .dialog-title, .p-dialog-title, [data-pc-section="header"]',
  )
  if (!header) return null

  const dialog = header.closest('.p-dialog')
  if (dialog instanceof HTMLElement) return dialog

  const named = header.closest('[data-pc-name="dialog"]')
  if (named instanceof HTMLElement && named.querySelector('.p-dialog-header')) return named

  const parent = header.parentElement
  return parent instanceof HTMLElement ? parent : null
}

const readOffset = (el: HTMLElement): { x: number; y: number } => {
  const match = /translate3d\(\s*([-.\d]+)px\s*,\s*([-.\d]+)px/.exec(el.style.transform)
  if (match) {
    return { x: Number.parseFloat(match[1] ?? '0'), y: Number.parseFloat(match[2] ?? '0') }
  }
  return { x: 0, y: 0 }
}

const writeOffset = (el: HTMLElement, x: number, y: number) => {
  el.style.setProperty('transform', `translate3d(${x}px, ${y}px, 0)`, 'important')
}

/**
 * Drag platform dialogs. Listens on document (capture) so PrimeVue header
 * mousedown / CSS zoom cannot swallow the gesture. Moves via transform.
 */
export function usePlatformDialogDrag(_options: UsePlatformDialogDragOptions = {}) {
  let session: DragSession | null = null

  const stopDrag = () => {
    session = null
    if (typeof document !== 'undefined') {
      document.removeEventListener('mousemove', onMouseMove, true)
      document.removeEventListener('mouseup', onMouseUp, true)
    }
    setDocumentDragging(false)
  }

  const onMouseMove = (event: MouseEvent) => {
    if (!session) return
    writeOffset(
      session.dialogEl,
      session.originX + visualToCss(event.clientX - session.pointerX, session.zoom),
      session.originY + visualToCss(event.clientY - session.pointerY, session.zoom),
    )
    event.preventDefault()
    event.stopImmediatePropagation()
  }

  const onMouseUp = () => {
    stopDrag()
  }

  const startDrag = (event: MouseEvent, dialog: HTMLElement) => {
    event.preventDefault()
    event.stopPropagation()
    event.stopImmediatePropagation()

    const origin = readOffset(dialog)
    session = {
      dialogEl: dialog,
      zoom: cssZoomOf(dialog, getUiScale()),
      originX: origin.x,
      originY: origin.y,
      pointerX: event.clientX,
      pointerY: event.clientY,
    }
    setDocumentDragging(true)
    document.addEventListener('mousemove', onMouseMove, true)
    document.addEventListener('mouseup', onMouseUp, true)
  }

  const onDocMouseDown = (event: MouseEvent) => {
    if (event.button !== 0) return
    if (session) return
    const dialog = resolveDialogFromEvent(event)
    if (!dialog) return
    startDrag(event, dialog)
  }

  if (typeof document !== 'undefined') {
    document.addEventListener('mousedown', onDocMouseDown, true)
  }

  onUnmounted(() => {
    if (typeof document !== 'undefined') {
      document.removeEventListener('mousedown', onDocMouseDown, true)
    }
    stopDrag()
  })

  return { onHeaderMouseDown: onDocMouseDown }
}
